/*
 * PX — Page panier (/cart). Web Component <cart-page>.
 *
 *  - Quantité et retrait en AJAX, sans rechargement, avec mise à jour optimiste.
 *  - Après chaque mutation, re-rendu du bloc .pxc-contents via la Section Rendering API
 *    (source de vérité serveur : lignes, jauge, totaux, remises).
 *  - Retrait avec toast « Annuler » 5 s : l'appel API ne part qu'à l'expiration.
 *  - Port / jauge / date de livraison : réutilise window.PxCart (défini par cart-drawer.js).
 *    AUCUNE copie de la grille ici.
 *  - Délégation d'événements sur le composant : survit aux re-rendus de .pxc-contents.
 */
(function () {
  'use strict';

  var DELAI_DEBOUNCE = 300;
  var DELAI_UNDO = 5000;

  function debounce(fn, ms) {
    var t;
    return function () {
      var a = arguments, c = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(c, a); }, ms);
    };
  }

  class PxCartPage extends HTMLElement {
    connectedCallback() {
      if (this.__init) return;
      this.__init = true;
      this.sectionId = this.dataset.id;
      this.aborts = {};
      this.pendingRemove = {};
      this.strings = {
        retire: '[titre] retiré du panier',
        annuler: 'Annuler',
        erreur: 'Une erreur est survenue. Réessayer',
        stockMax: 'Stock maximum atteint pour cet article.',
      };
      this._bind();
      // window.PxCart vient de cart-drawer.js (chargé à la fin du body) : si pas encore prêt,
      // on attend l'event load où tous les scripts différés ont tourné.
      if (window.PxCart) this._majEta();
      else window.addEventListener('load', this._majEta.bind(this), { once: true });
    }

    _cfg() {
      return {
        palier: parseInt(this.dataset.palier, 10) || 6000,
        seuil: parseInt(this.dataset.seuil, 10) || 10000,
        bas: parseInt(this.dataset.bas, 10) || 190,
        haut: parseInt(this.dataset.haut, 10) || 590,
      };
    }

    /* ---------- date de livraison estimée ---------- */
    _majEta() {
      var el = this.querySelector('#PxcEta');
      if (!el || !window.PxCart) return;
      var f = window.PxCart.fenetreLivraison();
      el.textContent = 'Reçu entre le ' + f.debut + ' et le ' + f.fin;
    }

    /* ---------- re-rendu du bloc contenu ---------- */
    _swap(html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var src = doc.querySelector('.pxc-contents');
      var mine = this.querySelector('.pxc-contents');
      if (src && mine) {
        mine.innerHTML = src.innerHTML;
        this._majEta();
        this._majBadge();
      }
    }

    _resync() {
      var self = this;
      return fetch(window.routes.cart_url + '?section_id=' + this.sectionId)
        .then(function (r) { return r.text(); })
        .then(function (html) { self._swap(html); })
        .catch(function () {});
    }

    _majBadge() {
      // Récupère le compte depuis /cart.js pour tenir le badge navbar à jour.
      fetch(window.routes.cart_url + '.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (c) {
          var b = document.getElementById('ps-nav-count');
          if (b) { b.textContent = c.item_count; b.style.display = c.item_count > 0 ? '' : 'none'; }
        })
        .catch(function () {});
    }

    /* ---------- mutation quantité ---------- */
    _changer(key, quantite) {
      var ligne = this.querySelector('.pxc-row[data-key="' + key + '"]');
      if (!ligne) return;
      var avant = parseInt(ligne.dataset.qty, 10) || 0;
      if (quantite === avant) return;

      // Optimiste : la valeur affichée bouge tout de suite.
      var input = ligne.querySelector('[data-pxc-qty]');
      if (input) input.value = quantite;
      ligne.dataset.qty = String(quantite);
      if (quantite === 0) ligne.classList.add('is-removing');
      ligne.classList.add('is-busy');

      if (this.aborts[key]) this.aborts[key].abort();
      var ctrl = new AbortController();
      this.aborts[key] = ctrl;
      var self = this;

      fetch(window.routes.cart_change_url + '.js', {
        method: 'POST',
        signal: ctrl.signal,
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantite, sections: this.sectionId }),
      })
        .then(function (r) { return r.json(); })
        .then(function (res) {
          if (res.errors || res.status) throw new Error(res.description || res.errors);
          var servie = (res.items || []).filter(function (i) { return i.key === key; })[0];
          if (quantite > 0 && servie && servie.quantity < quantite) self._toast(self.strings.stockMax);
          if (res.sections && res.sections[self.sectionId]) self._swap(res.sections[self.sectionId]);
          else self._resync();
        })
        .catch(function (e) {
          if (e.name === 'AbortError') return;
          self._resync();
          self._toast(self.strings.erreur, true);
        });
    }

    /* ---------- retrait avec annulation ---------- */
    _retirer(key) {
      var ligne = this.querySelector('.pxc-row[data-key="' + key + '"]');
      if (!ligne || this.pendingRemove[key]) return;
      ligne.classList.add('is-removing');
      var self = this;
      var titre = ligne.dataset.title || '';
      var timer = setTimeout(function () {
        delete self.pendingRemove[key];
        self._changer(key, 0);
      }, DELAI_UNDO);
      this.pendingRemove[key] = timer;
      this._toast(this.strings.retire.replace('[titre]', titre), false, {
        label: this.strings.annuler,
        action: function () {
          clearTimeout(timer);
          delete self.pendingRemove[key];
          ligne.classList.remove('is-removing');
        },
      });
    }

    /* ---------- toasts ---------- */
    _toast(texte, estErreur, undo) {
      var zone = this.querySelector('#PxcToasts');
      if (!zone) return;
      var t = document.createElement('div');
      t.className = 'pxc-toast' + (estErreur ? ' is-err' : '');
      t.setAttribute('role', 'status');
      var span = document.createElement('span');
      span.textContent = texte;
      t.appendChild(span);
      if (undo) {
        var b = document.createElement('button');
        b.type = 'button';
        b.textContent = undo.label;
        b.addEventListener('click', function () { undo.action(); t.remove(); });
        t.appendChild(b);
      }
      zone.appendChild(t);
      setTimeout(function () { t.classList.add('is-out'); }, DELAI_UNDO - 300);
      setTimeout(function () { t.remove(); }, DELAI_UNDO);
    }

    /* ---------- câblage (délégation) ---------- */
    _bind() {
      var self = this;
      this.addEventListener('click', function (e) {
        var plus = e.target.closest('[data-pxc-plus]');
        var moins = e.target.closest('[data-pxc-minus]');
        if (plus || moins) {
          e.preventDefault();
          var l = e.target.closest('.pxc-row');
          var q = parseInt(l.dataset.qty, 10) || 0;
          if (moins && q <= 1) { self._retirer(l.dataset.key); return; }
          self._changer(l.dataset.key, q + (plus ? 1 : -1));
          return;
        }
        var del = e.target.closest('[data-pxc-remove]');
        if (del) {
          e.preventDefault();
          var l2 = e.target.closest('.pxc-row');
          if (l2) self._retirer(l2.dataset.key);
        }
      });

      this.addEventListener('input', debounce(function (e) {
        var input = e.target.closest('[data-pxc-qty]');
        if (!input) return;
        var l = input.closest('.pxc-row');
        var v = parseInt(input.value, 10);
        if (isNaN(v) || v < 0) return;
        if (v === 0) self._retirer(l.dataset.key); else self._changer(l.dataset.key, v);
      }, DELAI_DEBOUNCE));

      // Anti double-clic sur le paiement.
      var form = this.querySelector('#cart');
      if (form) form.addEventListener('submit', function () {
        var cta = self.querySelector('#PxcCheckout');
        if (cta) { cta.classList.add('is-loading'); setTimeout(function () { cta.disabled = true; }, 0); }
      });
    }
  }

  if (!customElements.get('cart-page')) customElements.define('cart-page', PxCartPage);
})();
